// src/components/layout/Navbar.jsx
import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { BarChart3, Menu, X } from 'lucide-react';
import Images from '../Images';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white'
      }`}
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-16">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <RouterLink to="/" className="flex items-center" onClick={()=>setMenuOpen(false)}>
              <img src={Images.logo} alt="logo" className="w-10 h-10 rounded-full object-cover shadow-sm" />
            </RouterLink>
            <span className="hidden sm:inline-block text-lg font-semibold text-gray-900">QuickSMS</span>
          </div>

          {/* Center Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-brand-orange transition"
            >
              About Us
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="text-gray-700 hover:text-brand-orange transition"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-brand-orange transition"
            >
              Contact Us
            </button>
            <RouterLink to="/team" className="text-gray-700 hover:text-brand-orange transition">
              Team
            </RouterLink>
            <RouterLink to="/blog" className="text-gray-700 hover:text-brand-orange transition">
              Blog
            </RouterLink>
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center space-x-6"> 
            <button onClick={() => window.open('https://www.quicksmsgh.com', '_blank')} className="text-gray-700 hover:text-brand-orange transition cursor-pointer">
              Sign Up
            </button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={()=>window.open('https://www.quicksmsgh.com/register', '_blank')}
              className="bg-brand-orange text-white px-6 py-2 rounded-full hover:bg-brand-orange-hover transition"
            >
              Login
            </motion.button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMenuOpen((s) => !s)}
              aria-label="Toggle menu"
              className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md shadow-md border-t">
          <div className="px-6 pt-4 pb-6 space-y-4 flex flex-col">
            <button onClick={() => { scrollToSection('about'); setMenuOpen(false); }} className="text-left text-gray-700 hover:text-brand-orange">About Us</button>
            <button onClick={() => { scrollToSection('services'); setMenuOpen(false); }} className="text-left text-gray-700 hover:text-brand-orange">Services</button>
            <button onClick={() => { scrollToSection('contact'); setMenuOpen(false); }} className="text-left text-gray-700 hover:text-brand-orange">Contact Us</button>
            <RouterLink to="/team" onClick={()=>setMenuOpen(false)} className="text-gray-700 hover:text-brand-orange">Team</RouterLink>
            <RouterLink to="/blog" onClick={()=>setMenuOpen(false)} className="text-gray-700 hover:text-brand-orange">Blog</RouterLink>
            <div className="pt-2 border-t mt-2 flex flex-col gap-2">
              <button onClick={() => window.open('https://www.quicksmsgh.com', '_blank')} className="text-gray-700 hover:text-brand-orange text-left">Sign Up</button>
              <button onClick={()=>window.open('https://www.quicksmsgh.com/register', '_blank') } className="bg-brand-orange text-white px-4 py-2 rounded-full">Login</button>
            </div>
          </div>
        </div>
      )}
    </motion.nav>
  );
};

export default Navbar;